import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { API_URL } from "../../constants";
import { fetchPersonnelExpenses, PersonnelExpense } from "services/personnel.service";
import { formatDay } from "./personnel-helpers";
import { panelClass, TablePagination } from "./PersonnelCardParts";
import { FuelStatusChip } from "./PersonnelFuelReportsTab";

const cardClass = "rounded-lg border border-gray-200 bg-white shadow-sm";
const thClass = "px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500";
const tdClass = "px-3 py-2 align-top text-sm text-gray-700";

/** 'fuel' | 'other'; anything else is shown as sent. */
function categoryLabel(category: string | null, t: (key: string, opts?: any) => string): string {
  if (!category) return "—";
  return t(`personnel_expense_category_${category}`, { defaultValue: category });
}

const amount = (value: number | null, currency: string | null): string =>
  value == null ? "—" : `${value.toLocaleString("lt-LT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency || ""}`.trim();

/**
 * «Išlaidos»: this person's manual expenses (fuel + other), read only,
 * paginated. No module route exists today to link a row into (unlike
 * business trips / fuel reports) — rows are plain text. Documents (when the
 * viewer holds the module permission, per the backend's `fileId` gating)
 * open in a new tab rather than the PdfView modal — receipts can be a JPG or
 * PNG photo, not just a PDF (fuel-report-view-container.tsx does the same).
 */
export default function PersonnelExpensesTab({ companyId, userId }: { companyId: string; userId: string }) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [data, setData] = useState<{ rows: PersonnelExpense[]; total: number; limit: number } | null>(null);
  const [failed, setFailed] = useState(false);
  const requestRef = useRef(0);

  useEffect(() => {
    setPage(1);
  }, [companyId, userId]);

  const load = useCallback(() => {
    const requestId = ++requestRef.current;
    setFailed(false);
    fetchPersonnelExpenses(companyId, userId, page)
      .then((result) => {
        if (requestId === requestRef.current) setData(result);
      })
      .catch(() => {
        if (requestId === requestRef.current) setFailed(true);
      });
  }, [companyId, userId, page]);

  useEffect(() => {
    setData(null);
    load();
  }, [load]);

  const openDocument = (fileId: string) => {
    window.open(`${API_URL}/${companyId}/files/download/${fileId}?mode=view`, "_blank");
  };

  if (failed) {
    return (
      <div className={clsx(panelClass, "flex items-center gap-3 text-sm text-red-700")}>
        {t("personnel_expenses_load_failed")}
        <button type="button" className="underline" onClick={load}>
          {t("retry")}
        </button>
      </div>
    );
  }
  if (!data) return <p className="text-sm text-gray-500">{t("loading")}</p>;

  return (
    <section className={cardClass}>
      {data.rows.length === 0 ? (
        <p className="p-4 text-sm text-gray-500">{t("personnel_expenses_none")}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead>
              <tr>
                <th className={thClass}>{t("date")}</th>
                <th className={thClass}>{t("personnel_expense_category")}</th>
                <th className={thClass}>{t("amount")}</th>
                <th className={thClass}>{t("status")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.rows.map((expense) => (
                <tr key={expense.id}>
                  <td className={clsx(tdClass, "whitespace-nowrap tabular-nums")}>{formatDay(expense.date) || "—"}</td>
                  <td className={tdClass}>{categoryLabel(expense.category, t)}</td>
                  <td className={clsx(tdClass, "whitespace-nowrap tabular-nums")}>{amount(expense.amount, expense.currency)}</td>
                  <td className={tdClass}>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <FuelStatusChip status={expense.status} />
                      {expense.fileId && (
                        <button
                          type="button"
                          className="text-xs font-medium text-blue-700 hover:underline max-md:py-2 max-md:text-sm"
                          onClick={() => openDocument(expense.fileId!)}
                        >
                          {t("view")}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <TablePagination page={page} limit={data.limit} total={data.total} onPageChange={setPage} />
    </section>
  );
}
